import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DynamicGmap } from '@kyruus/gmap';
import { AwsMap } from '@kyruus/aws-map';
import { MapContainer } from './styles';

// Constants for map providers
const MAP_PROVIDERS = {
  AWS: 'aws',
  GOOGLE: 'google'
};

/**
 * ProviderMapContainer component displays a map view next to the provider list
 * when the provider_list_maps_enabled feature flag is enabled
 * Shows all provider locations as pins on the map
 *
 * Expected data structure for providers:
 * providers = [
 *   {
 *     id: string,
 *     name: string,
 *     locations: [
 *       {
 *         id: string,
 *         name: string,
 *         address: {
 *           coordinates: {
 *             lat: number,
 *             lon: number
 *           }
 *         }
 *       }
 *     ]
 *   }
 * ]
 */
function ProviderMapContainer({ providers, config, appSettings }) {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const mapBoundsRef = useRef(null);
  const [mapLocations, setMapLocations] = useState([]);
  const [locationIdsSet, setLocationIdsSet] = useState(new Set());

  // Cleanup function to prevent memory leaks when component unmounts
  useEffect(() => {
    return () => {
      // Clean up refs when component unmounts
      if (mapRef.current) {
        mapRef.current = null;
      }
      if (googleMapRef.current) {
        googleMapRef.current = null;
      }
      if (mapBoundsRef.current) {
        mapBoundsRef.current = null;
      }
    };
  }, []);

  /**
   * Generic function to transform provider location data
   * Renders only unique locations on the map rather than all provider-location combinations
   * @param {Array} providers - Array of provider objects with location data
   * @param {Function} coordinateMapper - Function to transform coordinates into the correct format
   * @returns {Array} - Formatted unique locations for map display
   */
  const transformProviderLocations = (providers, coordinateMapper) => {
    // First collect all locations with their coordinates
    const allLocations = providers.flatMap((provider) =>
      (provider.locations || [])
        .filter((location) => {
          // Check for both direct coordinates and address.coordinates structures
          return (
            location &&
            (location.coordinates ||
              (location.address && location.address.coordinates))
          );
        })
        .map((location) => ({
          id: location.id,
          name: location.name || provider.name,
          coordinates: coordinateMapper(location)
        }))
    );

    // Use a Map to deduplicate locations by ID
    const uniqueLocationsMap = new Map();

    allLocations.forEach((location) => {
      // Only add location if it doesn't already exist in the map
      if (!uniqueLocationsMap.has(location.id)) {
        uniqueLocationsMap.set(location.id, {
          id: location.id,
          name: `View providers at ${location.name}`,
          coordinates: location.coordinates
        });
      }
    });

    // Convert Map values to array
    return Array.from(uniqueLocationsMap.values());
  };

  /**
   * Transforms provider location data for Google Map
   * @param {Array} providers - Array of provider objects with location data
   * @returns {Array} - Formatted locations for Google Map
   */
  const transformLocationsForGoogleMap = (providers) => {
    return transformProviderLocations(providers, (location) => {
      // Handle both direct coordinates and coordinates inside address
      const coords =
        location.coordinates ||
        (location.address && location.address.coordinates);
      if (!coords) return { lat: 0, lon: 0 }; // Fallback if no coordinates found

      // Return the coordinates in the format Google Maps expects
      return {
        lat: coords.lat,
        lon: coords.lon || coords.lng
      };
    });
  };

  /**
   * Transforms provider location data for AWS Map
   * @param {Array} providers - Array of provider objects with location data
   * @returns {Array} - Formatted locations for AWS Map
   */
  const transformLocationsForAwsMap = (providers) => {
    return transformProviderLocations(providers, (location) => {
      // Handle both direct coordinates and coordinates inside address
      const coords =
        location.coordinates ||
        (location.address && location.address.coordinates);
      if (!coords) return { lat: 0, lng: 0 }; // Fallback if no coordinates found

      // AWS Map uses lng instead of lon for longitude
      return {
        lat: coords.lat,
        lng: coords.lon || coords.lng
      };
    });
  };

  // Process provider data when it changes (including pagination)
  useEffect(() => {
    if (!providers || !config) {
      return;
    }

    // Extract, filter, and format all valid locations from providers for Google Map
    // We'll use this for the Google Maps implementation
    // Call the memoized function with providers as argument
    const formattedLocations = transformLocationsForGoogleMap(providers);

    // Update state with new locations
    setMapLocations(formattedLocations);
    setLocationIdsSet(new Set(formattedLocations.map((loc) => loc.id)));
  }, [providers, config]);

  // If there are no providers or no config, return null
  if (!providers || !config || mapLocations.length === 0) {
    return null;
  }

  // Check if AWS Map should be used based on configuration
  const mapProvider = config?.maps_config?.location_service_provider;
  const useAwsMap = mapProvider === MAP_PROVIDERS.AWS;

  // Check if AWS credentials are available when AWS Map is selected
  const hasAwsCredentials = !!(
    appSettings?.AWS_MAPS_CREDENTIALS?.mapName &&
    appSettings?.AWS_MAPS_CREDENTIALS?.mapRegion &&
    appSettings?.AWS_MAPS_CREDENTIALS?.mapIdentityPoolId
  );

  // If AWS Map is selected but credentials are missing, fall back to Google Maps
  if (useAwsMap && !hasAwsCredentials) {
    // Add warning in development mode to help with debugging configuration issues
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        'AWS Map selected but credentials are missing. Falling back to Google Maps.'
      );
    }
    // Option 1: Fall back to Google Maps (what we're doing)
    // Option 2: Return null to prevent rendering with incomplete configuration
    // return null;
  }

  // Render AWS Map if selected and credentials are available
  if (useAwsMap && hasAwsCredentials) {
    // Transform locations for AWS Map
    // Call the memoized function with providers as argument
    const awsMapLocations = transformLocationsForAwsMap(providers);

    return (
      <MapContainer data-testid="provider-aws-map-container">
        <AwsMap
          awsMapConfig={{
            identityPoolId: appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
            mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
            region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
          }}
          locations={awsMapLocations}
          // Register map events for AWS Map if needed
          mapEventsToRegister={
            {
              // Add AWS Map specific event handlers here
            }
          }
        />
      </MapContainer>
    );
  }

  // Default to Google Maps if AWS Map is not selected or credentials are missing
  return (
    <MapContainer data-testid="provider-google-map-container">
      <DynamicGmap
        locations={mapLocations}
        enabledLocationIds={locationIdsSet}
        mapOptions={{
          maxZoom: 16,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: true,
          streetViewControl: true,
          styles: [
            // Simplify points of interest
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            // Simplify road labels
            {
              featureType: 'road',
              elementType: 'labels.icon',
              stylers: [{ visibility: 'off' }]
            },
            // Keep transit visible but simplified
            { featureType: 'transit', stylers: [{ visibility: 'simplified' }] }
          ]
        }}
        shouldLabelMarkers={true}
        theme={config.theme}
        getMarkers={(markers) => {
          // Store markers for potential future use
          mapRef.current = markers;
        }}
        mapEventsToRegister={{
          // Fit bounds when map is fully loaded
          idle: (mapInstanceRef) => {
            // Only fit bounds once when the map first loads
            if (
              mapInstanceRef.current &&
              mapLocations.length > 0 &&
              !googleMapRef.current
            ) {
              googleMapRef.current = mapInstanceRef.current;

              // Create bounds object if google is available
              if (window.google && window.google.maps) {
                const bounds = new window.google.maps.LatLngBounds();

                // Add each location to the bounds
                mapLocations.forEach((location) => {
                  bounds.extend({
                    lat: location.coordinates.lat,
                    lng: location.coordinates.lon
                  });
                });

                // Store bounds for future reference
                mapBoundsRef.current = bounds;

                // Fit the map to the bounds with padding
                mapInstanceRef.current.fitBounds(bounds, {
                  // Add padding to ensure markers aren't right at the edge
                  top: 50,
                  right: 50,
                  bottom: 50,
                  left: 50
                });
              }
            }
          }
        }}
      />
    </MapContainer>
  );
}

ProviderMapContainer.propTypes = {
  // Array of provider objects with location data
  providers: PropTypes.array,
  // Configuration object with theme settings
  config: PropTypes.object,
  // Application settings with AWS map credentials
  appSettings: PropTypes.object
};

ProviderMapContainer.defaultProps = {
  providers: [],
  config: null,
  appSettings: null
};

export default ProviderMapContainer;
